import { useRef, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';

import { constants, types } from './definitions';
import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import { formatTextWithHighlights } from '../../utils';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

function useGetQuerySuggestion(): types.UseGetQuerySuggestionReturn {
  const [querySuggestionResults, updateQuerySuggestionResults] = useState<
    string[]
  >([]);

  // using useRef to store the debounced function across renders
  const debouncedSearchSuggestionsRef = useRef(
    debounce(
      async (value) => {
        try {
          const response = await SupportApi.searchSuggestions(value);

          const querySuggestions = response.data.suggestions
            ? response.data.suggestions.map((suggestion) =>
                formatTextWithHighlights(suggestion.value.text),
              )
            : [];

          updateQuerySuggestionResults(querySuggestions);
        } catch (err) {
          // No need for toast should fail silently
          logger.error(`${constants.LOG_PREFIX}::${err}`);
        }
      },
      constants.DEBOUNCE_TIMEOUT,
      { leading: true },
    ),
  );

  /**
   * This useEffect hook watches for any ref changes to
   * debouncedSearchSuggestionsRef (via a useRef hook). When the
   * "current" value of the ref changes, this means a new request
   * for query suggestions has been made, so we cancel the in-flight
   * request because the user has updated their query and we want to
   * fetch based on the most recent input value.
   */
  useEffect(() => {
    return () => {
      debouncedSearchSuggestionsRef.current.cancel();
    };
  }, [debouncedSearchSuggestionsRef.current]);

  return {
    querySuggestionResults,
    debouncedSearchSuggestionsRef,
    updateQuerySuggestionResults,
  };
}

export default useGetQuerySuggestion;
